import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { PrimeNG } from 'primeng/config';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile, startWith } from 'rxjs/operators';
import { GLOBAL_TOAST } from '../../../../shared/constants/global-key.constans';
import { BackgroundJobService } from '../../../../services/background-job.service';
import { ExcelUploadProcessingResponse } from '../../../../models/excel-upload-processing-response.model';

export interface FileUploadConfig {
  title: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  jobId: string;
  uploadMethod: (file: File) => any;
}

@Component({
    selector: 'app-file-upload-card',
    templateUrl: './file-upload-card.component.html',
    styleUrls: ['./file-upload-card.component.scss'],
    standalone: false
})
export class FileUploadCardComponent implements OnInit, OnDestroy {
  @Input() config!: FileUploadConfig;
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  private readonly TOAST_LIFE = 5000;
  private readonly TOAST_LIFE_SHORT = 3000;
  private readonly POLL_INTERVAL = 3000;

  selectedFile: File | null = null;
  jobRunning: boolean = false;
  isSending: boolean = false;
  result: ExcelUploadProcessingResponse | null = null;
  showStatus: boolean = false;
  private jobSubscription: Subscription | null = null;

  constructor(
    private messageService: MessageService,
    private config_: PrimeNG,
    private backgroundJobService: BackgroundJobService
  ) {}

  ngOnInit() {
    this.startJobPolling(this.config.jobId, true);
  }

  onFileSelected(event: any) {
    this.selectedFile = event.files[0];
    this.messageService.add({
      key: GLOBAL_TOAST,
      severity: 'success',
      summary: 'Fichier sélectionné',
      detail: `${this.config.title}: ${this.selectedFile?.name}`,
      life: this.TOAST_LIFE_SHORT,
    });
  }

  removeFile() {
    this.selectedFile = null;
    this.messageService.add({
      key: GLOBAL_TOAST,
      severity: 'info',
      summary: 'Fichier supprimé',
      detail: `${this.config.title} retiré`,
      life: this.TOAST_LIFE_SHORT,
    });
  }

  sendFile() {
    if (this.selectedFile) {
      this.isSending = true;
      this.messageService.add({
        key: GLOBAL_TOAST,
        severity: 'info',
        summary: 'Envoi en cours',
        detail: `${this.config.title} en cours de traitement...`,
        life: this.TOAST_LIFE,
      });

      this.config.uploadMethod(this.selectedFile).subscribe({
        next: (response: any) => {
          this.isSending = false;
          this.selectedFile = null;
          this.messageService.add({
            key: GLOBAL_TOAST,
            severity: 'success',
            summary: 'Succès',
            detail: `${this.config.title} envoyé avec succès`,
            life: this.TOAST_LIFE,
          });

          this.result = response;
          this.jobRunning = true;
          this.showStatus = true;
          this.startJobPolling(response.IngestionJobId, false);
        },
        error: (_error: any) => {
          this.isSending = false;
          this.messageService.add({
            key: GLOBAL_TOAST,
            severity: 'error',
            summary: 'Erreur',
            detail: `Erreur lors de l'envoi ${this.config.title}`,
            life: this.TOAST_LIFE,
          });
        },
      });
    }
  }

  private startJobPolling(jobId: string, isInitial = false): void {
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
    }

    this.jobSubscription = interval(this.POLL_INTERVAL)
      .pipe(
        startWith(0),
        switchMap(() => this.backgroundJobService.getBackgroundJob(jobId)),
        takeWhile((status) => status.isRunning, true)
      )
      .subscribe({
        next: (status) => {
          this.showStatus = true;
          this.jobRunning = status.isRunning;

          if (!status.isRunning && !isInitial) {
            this.messageService.add({
              key: GLOBAL_TOAST,
              severity: 'success',
              summary: 'Traitement terminé',
              detail: `Le traitement ${this.config.title} est terminé`,
              life: this.TOAST_LIFE,
            });
          }
        },
        error: (_error: any) => {
          this.jobRunning = false;
        },
      });
  }

  ngOnDestroy() {
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
    }
  }

  formatSize(bytes: any) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config_.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes?.[0] ?? 'B'}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes?.[i] ?? 'B'}`;
  }
}
